import { Stack, FileButton, Button, Text } from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import { IconFileUpload } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { trackEvent } from "../../common/analytics";
import { importMembers } from "../../common/api";
import { useBoard } from "../../common/boards/board.hooks";
import { CreateMemberDto } from "../../common/members/dto/create-member.dto";
import { Member } from "../../common/members/member.schema";
import PapaParse from "papaparse";
interface CSVRow {
  firstName?: string;
  lastName?: string;
  club?: string;
  country?: string;
  email?: string;
  usfaMemberNumber?: string;
}
const ImportMembers = ({
  onImport,
}: {
  onImport?: (members: Member[]) => void;
}) => {
  const { board } = useBoard();
  const queryClient = useQueryClient();
  const { mutate: saveMembers, isLoading: creating } = useMutation(
    importMembers,
    {
      onSuccess(res) {
        // console.log("res :", res);
        // console.log("creatingMembers :", creatingMembers);
        const members: Member[] = res.members;
        let message = "";
        if (res.created.length > 0) {
          message =
            res.created.length === 1
              ? "member created"
              : `${res.created.length} members created`;
        }
        if (res.existing.length > 0) {
          if (message.length > 0) message += ", ";
          message +=
            res.existing.length === 1
              ? "member found"
              : `${res.existing.length} members found`;
        }
        showNotification({
          message: message,
          autoClose: true,
          color: "success",
        });
        if (members.length === 0) return;
        onImport && onImport(members);
        queryClient.invalidateQueries(["members"]);
        //   setValue(member._id);
      },
    }
  );
  const selectImportFile = async (file: File | null) => {
    if (!board || !file) return;
    trackEvent("select_import_file", { board: board._id });
    let text = await file.text();
    // console.log("text :", text);
    //strip empty lines
    text = text
      .split("\n")
      .filter((line) => line.trim().length > 0)
      .join("\n");
    // console.log("text :", text);
    // console.log("text :", text);
    const res = PapaParse.parse<CSVRow>(text, {
      header: true,
      delimiter: ",",
    });
    // console.log("res :", res);
    let valid = true;
    // console.log("res.data.length :", res.data.length);
    if (res.data.length === 0) {
      valid = false;
      // console.log("showing error pop");
      openConfirmModal({
        title: "No users in CSV",
        children: (
          <Text size="sm">
            The selected CSV file does not contain any valid users
          </Text>
        ),
        labels: { confirm: "Close", cancel: "Cancel" },
        cancelProps: { display: "none" },
      });
      return;
    }
    const invalidLines = [];
    const members: CreateMemberDto[] = res.data.map((d, i) => {
      const { firstName, lastName, email, usfaMemberNumber, club, country } = d;
      if (!firstName && !usfaMemberNumber) {
        valid = false;
        invalidLines.push(i);
      } else {
        return {
          name: firstName,
          lastName,
          email,
          usfaMemberNumber,
          club,
          country,
        };
      }
    }) as CreateMemberDto[];
    // console.log("members :", members);
    if (valid && members.length > 0) {
      saveMembers({ boardId: board?._id, members });
    } else {
      openConfirmModal({
        title: "Invalid users in CSV",
        children: (
          <Text size="sm">
            {invalidLines.length > 0} rows in CSV file do not contain a valid
            users
          </Text>
        ),
        labels: { confirm: "Close", cancel: "Cancel" },
        cancelProps: { display: "none" },
      });
    }
  };
  return (
    <Stack>
      <FileButton onChange={selectImportFile} accept="text/csv">
        {(props) => (
          <Button
            {...props}
            leftSection={<IconFileUpload size={18} />}
            variant="default"
            loading={creating}
            disabled={creating}
          >
            Import users from csv
          </Button>
        )}
      </FileButton>
      <Text size="xs" color="dimmed">
        <p>
          Import a list of users from an existing .csv file. Columns used are
          firstName, lastName, and optionally email, club, country, and
          usfaMemberNumber.
        </p>
        <p>
          Users must have at least firstName, or usfaMemberNumber in which case
          names will be copied from the US fencing database.
        </p>
        <p>usfaMemberNumber must be a valid 10 digit USFA membership number.</p>
        <p>Country must ne a two digit ISO country code.</p>
        <p>
          Imported users will be updated rather than created if any existing
          duplicates are found.
        </p>
      </Text>
    </Stack>
  );
};
export default ImportMembers;
